/**
 * Formatting and input parsing for the phone's money screens.
 *
 * Two rules run through this file:
 *
 *  1. **Every amount is integer piastres.** Text typed by a human is converted
 *     to piastres by exact integer arithmetic — never `parseFloat(x) * 100`,
 *     which rounds 12.34 to 1233.9999999999998 before anyone gets to round it.
 *  2. **Nothing here reads the ambient clock.** Instants arrive as corrected
 *     UTC epoch milliseconds from the TrustedClock and are rendered through the
 *     domain's own Cairo reckoning, which never consults the platform timezone.
 */

import {
  type DayKey,
  type Piastres,
  DEFAULT_ROLLOVER_HOUR,
  daysBetween,
  formatCairoDate,
  formatCairoTime12,
  formatEgp,
  piastres,
  rolloverDayOf,
} from '@office/domain'
import { parseDayKey } from '@office/domain/money'

export { formatEgp }

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

/**
 * A human amount, in piastres, or null when the text is not a usable amount.
 *
 * Accepts thousands separators and up to two decimal places. Rejects negatives:
 * an expense, a bill payment and a settlement are all constrained to be
 * positive, and a minus sign in an amount field is far more often a typo than
 * an intention.
 */
export function parseAmount(text: string): Piastres | null {
  const cleaned = text.replace(/[,\s]/g, '')
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  const dot = cleaned.indexOf('.')
  const whole = dot === -1 ? cleaned : cleaned.slice(0, dot)
  const frac = dot === -1 ? '' : cleaned.slice(dot + 1)
  const wholeN = Number(whole)
  if (!Number.isSafeInteger(wholeN)) return null
  const fracN = Number(`${frac}00`.slice(0, 2))
  const total = wholeN * 100 + fracN
  return Number.isSafeInteger(total) ? piastres(total) : null
}

/** The editable form of an amount: `1234.50`, no currency, no separators. */
export function amountInputValue(p: Piastres): string {
  const neg = p < 0
  const abs = Math.abs(p)
  return `${neg ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

/** `3 Mar 2026`. Built from the DayKey's own parts — never through `toLocaleDateString`. */
export function formatDay(day: DayKey): string {
  const { year, month, day: dayOfMonth } = parseDayKey(day)
  return `${dayOfMonth} ${MONTHS[month - 1] ?? '???'} ${year}`
}

/** `3 Mar 2026 14:20`, in Cairo wall-clock time, computed rather than looked up. */
export function formatInstant(utcMs: number): string {
  return `${formatDay(formatCairoDate(utcMs) as DayKey)} ${formatCairoTime12(utcMs)}`
}

/**
 * How a due date reads relative to the office's own day (docs/01 ⚑): a bill due
 * today is still "due today" at 02:00 the following calendar night, because the
 * office day has not rolled over.
 */
export function describeDue(
  day: DayKey,
  now: number,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): string {
  const diff = daysBetween(rolloverDayOf(now, rolloverHour), day)
  if (diff === 0) return 'due today'
  if (diff === 1) return 'due tomorrow'
  if (diff > 1) return `due in ${diff} days`
  if (diff === -1) return '1 day overdue'
  return `${-diff} days overdue`
}

/** `EGP 12.00` — the one place a piastre value becomes text for a human. */
export const money = (p: Piastres): string => formatEgp(p)
